import { supabase } from "@/lib/supabase";

export type InstallerComplianceReviewDocument = {
  id: string;
  organization_id: string | null;
  contractor_id: string;
  installer_user_id: string | null;
  type: string | null;
  file_name: string | null;
  mime_type: string | null;
  byte_size: number | null;
  storage_path: string | null;
  uploaded_at: string | null;
  expires_at: string | null;
  verified_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  supersedes_id: string | null;
  superseded_by: string | null;
  created_at: string | null;
  contractor_company_name?: string | null;
  contractor_crew_id?: string | null;
  contractor_crew_status?: string | null;
  contractor_crew_role?: string | null;
  installer_crew_name?: string | null;
  installer_full_name?: string | null;
  installer_email?: string | null;
  verified_by?: string | null;
  rejected_by?: string | null;
};

export type InstallerComplianceCrewRosterRow = {
  id: string;
  contractor_id: string;
  user_id: string | null;
  linked_user_id?: string | null;
  crew_role: string | null;
  status: string | null;
  full_name?: string | null;
  email?: string | null;
};

async function getFunctionError(data: unknown, error: unknown): Promise<string | null> {
  if (data && typeof data === "object" && "error" in data) {
    return String((data as { error?: unknown }).error ?? "installer_compliance_review_failed");
  }
  const context = (error as { context?: unknown } | null)?.context;
  if (context instanceof Response) {
    const payload = await context.clone().json().catch(() => null) as { error?: unknown; message?: unknown } | null;
    if (payload?.error) return String(payload.error);
    if (payload?.message) return String(payload.message);
  }
  const message = (error as { message?: unknown } | null)?.message;
  if (typeof message === "string" && message.trim()) return message;
  return null;
}

function isFunctionNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as Record<string, unknown>;

  if (err.status === 404 || err.status === "404") return true;
  if (err.code === "function_not_found") return true;
  const message = String(err.message ?? "");
  if (/function.*not found|404|NOT_FOUND/i.test(message)) return true;

  const context = err.context;
  if (context instanceof Response && context.status === 404) return true;

  return false;
}

export async function listInstallerComplianceReviewDocuments(
  organizationId: string,
): Promise<InstallerComplianceReviewDocument[]> {
  try {
    const { data, error } = await supabase.functions.invoke("installer-compliance-review", {
      body: { action: "list", organizationId },
    });
    if (isFunctionNotFoundError(error)) return [];
    const message = await getFunctionError(data, error);
    if (message) throw new Error(message);
    return ((data as { documents?: InstallerComplianceReviewDocument[] } | null)?.documents ?? []);
  } catch (raw) {
    if (isFunctionNotFoundError(raw)) return [];
    const message = await getFunctionError(null, raw);
    if (message) throw new Error(message);
    throw raw;
  }
}

export async function listInstallerComplianceCrewRoster(
  contractorIds: string[],
): Promise<InstallerComplianceCrewRosterRow[]> {
  const normalizedIds = Array.from(new Set(contractorIds.map((value) => String(value ?? "").trim()).filter(Boolean)));
  if (normalizedIds.length === 0) return [];
  try {
    const { data, error } = await supabase.functions.invoke("installer-compliance-review", {
      body: { action: "list_roster", contractorIds: normalizedIds },
    });
    if (isFunctionNotFoundError(error)) return [];
    const message = await getFunctionError(data, error);
    if (message) throw new Error(message);
    return ((data as { crews?: InstallerComplianceCrewRosterRow[] } | null)?.crews ?? []);
  } catch (raw) {
    if (isFunctionNotFoundError(raw)) return [];
    const message = await getFunctionError(null, raw);
    if (message) throw new Error(message);
    throw raw;
  }
}

export async function approveInstallerComplianceDocument(documentId: string): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke("installer-compliance-review", {
      body: { action: "approve", documentId },
    });
    if (isFunctionNotFoundError(error)) return;
    const message = await getFunctionError(data, error);
    if (message) throw new Error(message);
  } catch (raw) {
    if (isFunctionNotFoundError(raw)) return;
    const message = await getFunctionError(null, raw);
    if (message) throw new Error(message);
    throw raw;
  }
}

export async function activateInstallerComplianceCrew(input: {
  organizationId: string;
  contractorId: string;
  contractorCrewId?: string | null;
  crewUserId?: string | null;
}): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke("installer-compliance-review", {
      body: {
        action: "activate_crew",
        organizationId: input.organizationId,
        contractorId: input.contractorId,
        contractorCrewId: input.contractorCrewId,
        crewUserId: input.crewUserId,
      },
    });
    if (isFunctionNotFoundError(error)) return;
    const message = await getFunctionError(data, error);
    if (message) throw new Error(message);
  } catch (raw) {
    if (isFunctionNotFoundError(raw)) return;
    const message = await getFunctionError(null, raw);
    if (message) throw new Error(message);
    throw raw;
  }
}

export async function rejectInstallerComplianceDocument(
  documentId: string,
  rejectionReason: string,
): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke("installer-compliance-review", {
      body: { action: "reject", documentId, rejectionReason },
    });
    if (isFunctionNotFoundError(error)) return;
    const message = await getFunctionError(data, error);
    if (message) throw new Error(message);
  } catch (raw) {
    if (isFunctionNotFoundError(raw)) return;
    const message = await getFunctionError(null, raw);
    if (message) throw new Error(message);
    throw raw;
  }
}

export async function getInstallerComplianceDocumentReadUrl(
  documentId: string,
  expiresInSeconds = 300,
): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke("installer-compliance-review", {
      body: { action: "read_url", documentId, expiresInSeconds },
    });
    if (isFunctionNotFoundError(error)) return "";
    const message = await getFunctionError(data, error);
    if (message) throw new Error(message);
    const url = String((data as { url?: unknown } | null)?.url ?? "").trim();
    if (!url) throw new Error("signed_url_missing");
    return url;
  } catch (raw) {
    if (isFunctionNotFoundError(raw)) return "";
    const message = await getFunctionError(null, raw);
    if (message) throw new Error(message);
    throw raw;
  }
}
